import { ChevronDown, ChevronUp, Clock, Tag } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { memo, useState } from 'react';

import { GetEventRegistrationsStatsQuery as GetEventRegistrationsStats } from '__generated__/graphql';
import { ProgressBar, StatsListContainer, StatsListFooter, StatsListRow } from './Layout';
import { UIProps } from '../../theme/mixins';
import { assetUrl } from '../../common/helpers';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useProject from '../useProject';
import useTimeSlotFormatter from '../../common/Tickets/useTimeSlotFormatter';

type Event = GetEventRegistrationsStats['event'];
type Ticket = Event['tickets'][0];

export type SalesColumnOption = 'sales' | 'reservations' | 'available';
export type RevenueColumnOption = 'revenue' | 'remaining' | 'average_selling_price';

export interface TicketStatsProps {
  event: Event;
  tickets: Ticket[];
  salesColumn?: SalesColumnOption;
  revenueColumn?: RevenueColumnOption;
}

const TicketStats = memo(({ event, tickets, salesColumn = 'sales', revenueColumn = 'revenue' } : TicketStatsProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const device = useMediaDevice();

  const limit = 5;
  const [showingAll, setShowingAll] = useState(false);
  const visibleTickets = showingAll ? tickets : tickets.slice(0, limit);

  const totals = tickets.reduce((totals, ticket) => {
    const ticketCapacity = ticket.capacity !== null ? ticket.capacity : ticket.indirect_capacity;

    return {
      sold_count: totals.sold_count + ticket.sold_count,
      invitation_reservations_count: totals.invitation_reservations_count + ticket.invitation_reservations_count,
      revenue: totals.revenue + ticket.revenue,
      remaining_amount: totals.remaining_amount + ticket.remaining_amount,
      active_purchases_count: totals.active_purchases_count + ticket.active_purchases_count,
      accepted_purchases_amount: totals.accepted_purchases_amount + ticket.accepted_purchases_amount,
      // If any of the tickets has unlimited capacity, then the total capacity is unlimited.
      capacity: totals.capacity === null || ticketCapacity === null ? null : totals.capacity + ticketCapacity,
    };
  }, {
    sold_count: 0,
    invitation_reservations_count: 0,
    revenue: 0,
    remaining_amount: 0,
    active_purchases_count: 0,
    accepted_purchases_amount: 0,
    capacity: 0 satisfies number | null,
  });

  return (
    <>
      {tickets.length > 0 && (
        <StatsListContainer>
          {visibleTickets.map((ticket) => (
            <TicketRow
              ticket={ticket}
              salesColumn={salesColumn}
              revenueColumn={revenueColumn}
              key={ticket.id}
            />
          ))}
          {(showingAll || tickets.length <= limit) && (
            <StatsListRow>
              <UI.Div sc={{ fontWeight: 500 }}>
                {t('totals')}
              </UI.Div>
              {!device.isSmall && <UI.Div />}
              <SalesColumn activeColumn={salesColumn} source={totals} hideCapacity />
              <RevenueColumn activeColumn={revenueColumn} source={totals} />
            </StatsListRow>
          )}
        </StatsListContainer>
      )}
      {tickets.length > limit && (
        <StatsListFooter>
          {!showingAll && (
            <UI.Button
              onClick={() => setShowingAll(true)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              {t('show_all')}
              {' '}
              <UI.Span sc={{ muted: true }}>
                {`(${tickets.length})`}
              </UI.Span>
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronDown />
              </UI.Icon>
            </UI.Button>
          )}
          {showingAll && (
            <UI.Button
              onClick={() => setShowingAll(false)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              {t('show_less')}
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronUp />
              </UI.Icon>
            </UI.Button>
          )}
        </StatsListFooter>
      )}
      {tickets.length === 0 && (
        <UI.Div style={{ borderTop: 0 }}>
          <UI.FlexContainer sc={{ justifyContent: 'center', mb: 4 }}>
            <img src={assetUrl('images/dashboard-tickets.svg')} height={100} alt={t('tickets')} />
          </UI.FlexContainer>
          <UI.Div sc={{ textAlign: 'center', color: 'gray.400' }}>
            <Trans i18nKey="no_tickets_created">
              <UI.Link to={`/${project.id}/events/${event.id}/tickets/add`} />
            </Trans>
          </UI.Div>
        </UI.Div>
      )}
    </>
  );
});

interface TicketRowProps {
  ticket: Ticket;
  salesColumn: SalesColumnOption;
  revenueColumn: RevenueColumnOption;
}

const TicketRow = ({ ticket, salesColumn, revenueColumn }: TicketRowProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();

  const hasExtraStats = ticket.promotions.length > 1 || ticket.time_slots.length > 0;
  const [showingExtraStats, setShowingExtraStats] = useState(false);

  const toggleExtraStats = () => setShowingExtraStats((showing) => !showing);

  const capacity = ticket.capacity !== null ? ticket.capacity : ticket.indirect_capacity;

  return (
    <UI.Div>
      <StatsListRow>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.FlexContainer>
            <UI.Truncate>
              <UI.Span sc={{ color: 'gray.800' }}>
                <UI.Icon sc={{ mr: 1 }}>
                  <UI.StatusIndicator sc={{ background: ticket.active ? 'success' : 'gray.300' }} />
                </UI.Icon>
                {ticket.title}
              </UI.Span>
            </UI.Truncate>
            {hasExtraStats && (
              <UI.Span>
                <UI.Button
                  sc={{ blank: true, size: 'xs', my: -0.75, ml: 1 }}
                  onClick={() => toggleExtraStats()}
                  aria-label={t('toggle_name', { name: ticket.title })}
                >
                  <UI.Icon>
                    {showingExtraStats && <ChevronUp />}
                    {!showingExtraStats && <ChevronDown />}
                  </UI.Icon>
                </UI.Button>
              </UI.Span>
            )}
          </UI.FlexContainer>
        </UI.Div>
        {!device.isSmall && (
          <UI.Div>
            {capacity > 0 && (
              <ProgressBar
                count={ticket.sold_count}
                capacity={capacity}
                secondary={ticket.invitation_reservations_count}
                tooltip={t('sales_plus_reservations')}
              />
            )}
          </UI.Div>
        )}
        <SalesColumn activeColumn={salesColumn} source={ticket} />
        <RevenueColumn activeColumn={revenueColumn} source={ticket} />
      </StatsListRow>
      {showingExtraStats && (
        <ExtraTicketStats ticket={ticket} salesColumn={salesColumn} revenueColumn={revenueColumn} />
      )}
    </UI.Div>
  );
};

const ExtraTicketStats = ({ ticket, salesColumn, revenueColumn }: TicketRowProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const device = useMediaDevice();
  const formatTimeSlot = useTimeSlotFormatter();

  return (
    <>
      {ticket.promotions.length > 1 && ticket.promotions.map((promotion) => {
        const startDate = promotion.start_date && formatDate(parseDate(promotion.start_date, { timezone: 'UTC' }), {
          format: 'display_compact_date',
        });

        const endDate = promotion.end_date && formatDate(parseDate(promotion.end_date, { timezone: 'UTC' }), {
          format: 'display_compact_date',
        });

        return (
          <StatsListRow sc={{ background: 'gray.50' }} key={promotion.id}>
            <UI.FlexContainer sc={{ pl: 4 }}>
              <UI.TooltipContainer style={{ minWidth: 0 }}>
                <UI.Truncate>
                  <UI.Icon sc={{ mr: 1 }}>
                    <Tag />
                  </UI.Icon>
                  {promotion.title || t('standard')}
                </UI.Truncate>
                {(startDate || endDate) && (
                  <UI.Tooltip sc={{ alignV: 'bottom', left: 20 }}>
                    {startDate && endDate && `${startDate} – ${endDate}`}
                      {startDate && !endDate && t('starts_on', { date: startDate })}
                      {!startDate && endDate && t('ends_on_date', { date: endDate })}
                  </UI.Tooltip>
                )}
              </UI.TooltipContainer>
            </UI.FlexContainer>
            {!device.isSmall && (
              <UI.Div>
                {promotion.capacity > 0 && (
                  <ProgressBar
                    count={promotion.sold_count}
                    capacity={promotion.capacity}
                    secondary={promotion.invitation_reservations_count}
                    tooltip={t('sales_plus_reservations')}
                  />
                )}
              </UI.Div>
            )}
            <SalesColumn activeColumn={salesColumn} source={promotion} />
            <RevenueColumn activeColumn={revenueColumn} source={promotion} />
          </StatsListRow>
        );
      })}
      {ticket.time_slots.length > 0 && ticket.time_slots.map((timeSlot) => (
        <StatsListRow sc={{ background: 'gray.50' }} key={timeSlot.id}>
          <UI.Div sc={{ pl: 4 }} style={{ minWidth: 0 }}>
            <UI.Truncate>
              <UI.Icon sc={{ mr: 1 }}>
                <Clock />
              </UI.Icon>
              <UI.Delimit>
                {formatTimeSlot(timeSlot)}
                {timeSlot.title}
              </UI.Delimit>
            </UI.Truncate>
          </UI.Div>
          {!device.isSmall && (
            <UI.Div>
              {timeSlot.capacity > 0 && (
                <ProgressBar
                  count={timeSlot.sold_count}
                  capacity={timeSlot.capacity}
                  secondary={timeSlot.invitation_reservations_count}
                  tooltip={t('sales_plus_reservations')}
                />
              )}
            </UI.Div>
          )}
          <SalesColumn activeColumn={salesColumn} source={timeSlot} />
          <RevenueColumn activeColumn={revenueColumn} source={timeSlot} />
        </StatsListRow>
      ))}
    </>
  );
};

interface SalesColumnProps {
  activeColumn: SalesColumnOption;
  source: {
    sold_count: number,
    invitation_reservations_count: number,
    capacity?: number | null,
    indirect_capacity?: number | null,
  };
  sc?: UIProps;
  hideCapacity?: boolean;
}

export const SalesColumn = ({
  activeColumn,
  source,
  sc = {},
  hideCapacity = false,
}: SalesColumnProps) => {
  const { formatInteger } = useLocale();
  const capacity = source.capacity !== null ? source.capacity : (source.indirect_capacity || null);
  const available = capacity !== null
    ? Math.max(0, capacity - source.sold_count - source.invitation_reservations_count)
    : null;

  return (
    <UI.Div sc={{ noWrap: true, ...sc }}>
      {activeColumn === 'sales' && formatInteger(source.sold_count)}
      {activeColumn === 'reservations' && formatInteger(source.invitation_reservations_count)}
      {activeColumn === 'available' && (available !== null ? formatInteger(available) : '∞')}
      {!hideCapacity && (
        <UI.Span sc={{ small: true, muted: true }}>
          {` / ${capacity !== null ? formatInteger(capacity) : '∞'}`}
        </UI.Span>
      )}
    </UI.Div>
  );
};

interface RevenueColumnProps {
  activeColumn: RevenueColumnOption;
  source: {
    revenue: number,
    remaining_amount: number,
    active_purchases_count: number,
    accepted_purchases_amount: number,
  };
}

export const RevenueColumn = ({ activeColumn, source }: RevenueColumnProps) => {
  const device = useMediaDevice();
  const { formatCurrency } = useLocale();

  return !device.isSmall && (
    <UI.Div sc={{ noWrap: true, muted: true }}>
      {activeColumn === 'revenue' && formatCurrency(source.revenue)}
      {activeColumn === 'remaining' && formatCurrency(source.remaining_amount)}
      {activeColumn === 'average_selling_price' && source.active_purchases_count > 0 && (
        formatCurrency(source.accepted_purchases_amount / source.active_purchases_count)
      )}
    </UI.Div>
  );
};

export default TicketStats;
